import React, { useState, useCallback, useContext } from 'react';
import { Prompt } from 'react-router';
import { Modal } from '@components/common';
import { sortBy, isEqual } from 'lodash';
import Sortable from 'react-sortablejs';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from '@store/policies/precisionPolicy/actions';
import { selector } from '@store/policies/precisionPolicy';
import track, { CLICK_SAVE_REORDER_POLICIES } from '@src/utils/sensor/track';
import { ViewTypes } from '@store/policies/precisionPolicy/reducers';
import { IPolicy, IResource, IPolicyVersion } from '../../types';
import EventContext from '../../EventContext';
import { Wrapper, Shares } from './styled';
import { PolicyWrapper, PolicyFilter, PolicyVersion } from '../../../common';
import Share from './PolicyShare';
import Buttons from './Buttons';
import Options from '../Options';
import { CLONE_LIMIT, LEAVE_CONFIRMATION } from '../../constants';
import ScheduleList from '../ScheduleList';

interface IProps {
  policies: IPolicy[];
  resources: IResource[];
  canEdit?: boolean;
  activeView: ViewTypes;
}

const getShares = (version: IPolicyVersion, resources: IResource[]) => (
  <Shares>
    <table>
      <thead>
        <tr>
          <th>Share</th>
          <th>Primary Resource</th>
          <th>Backup Resource(s)</th>
        </tr>
      </thead>
      <tbody>
        {version.shares.map((share) => (
          <Share key={share.id} share={share} resources={resources} />
        ))}
      </tbody>
    </table>
  </Shares>
);

const getRenderedPolicies = ({ policies, sortable, resources, canEdit }) =>
  policies.map((policy, idx) => (
    <PolicyWrapper data-id={policy.id} key={policy.id} sortable={sortable}>
      <PolicyFilter
        policy={policy}
        data-id={policy.id}
        rowIndex={idx + 1}
        index={policy.index}
        sortable={sortable}
      />
      {policy.versions
        .sort((a, b) => {
          if (a.is_active) {
            return -1;
          } else if (b.is_active) {
            return 1;
          } else {
            return a.id < b.id ? -1 : 1;
          }
        })
        .map((version) => {
          const shares = getShares(version, resources);

          const options = (
            <Options
              policy={policy}
              version={version}
              canClone={policy.versions.length < CLONE_LIMIT}
            />
          );

          return (
            <PolicyVersion
              key={version.id}
              policy={policy}
              version={version}
              canEdit={canEdit}
              options={options}
            >
              {shares}
            </PolicyVersion>
          );
        })}
    </PolicyWrapper>
  ));

const Policies: React.FC<IProps> = ({
  policies: unOrderedPolicies,
  resources,
  canEdit = false,
  activeView,
}) => {
  const { reorderPolicies } = useContext(EventContext);
  const [modalVisible, setModalVisible] = useState(false);
  const sortedPolices = sortBy(unOrderedPolicies, (p) => p.index);
  const originalOrder = sortedPolices.map((p) => p.id);
  const [order, setOrder] = useState(sortedPolices.map((p) => p.id));
  const { sortable } = useSelector(selector);
  const dispatch = useDispatch();
  const policies = sortBy(unOrderedPolicies, (p) => order.indexOf(p.id));

  const onCancel = useCallback(() => {
    setOrder(originalOrder);
    dispatch(actions.toggleSortable(false));
  }, [dispatch]);

  const onToggle = useCallback(() => {
    dispatch(actions.toggleSortable(true));
  }, [dispatch]);

  const onOk = useCallback(() => {
    setModalVisible(true);
  }, [setModalVisible]);

  const onLayoutChange = useCallback((newOrder) => {
    setOrder(newOrder.map((o) => parseInt(o, 10)));
  }, []);

  const onModalCancel = useCallback(() => {
    setModalVisible(false);
  }, [setModalVisible]);

  const onModalOk = useCallback(() => {
    track({ event: CLICK_SAVE_REORDER_POLICIES });
    reorderPolicies(order);
    setModalVisible(false);
  }, [order, reorderPolicies, setModalVisible]);

  const renderedPolicies = getRenderedPolicies({
    policies,
    sortable,
    resources,
    canEdit,
  });

  return (
    <>
      <Wrapper>
        <Buttons
          editMode={sortable}
          onOk={onOk}
          onCancel={onCancel}
          onToggle={onToggle}
          canEdit={canEdit}
          activeView={activeView}
        />
        {activeView === ViewTypes.policies && (
          <Sortable
            key={`Sortable-Policies-${sortable}`}
            options={{
              disabled: !sortable,
              ghostClass: 'ghost-version',
            }}
            onChange={onLayoutChange}
          >
            {renderedPolicies}
          </Sortable>
        )}
        {activeView === ViewTypes.schedules && <ScheduleList canEdit={canEdit} />}
        <Modal
          title="Confirm"
          visible={modalVisible}
          content="Are you sure to apply the new order?"
          onOk={onModalOk}
          onCancel={onModalCancel}
        />
      </Wrapper>
      <Prompt when={!isEqual(order, originalOrder)} message={`${LEAVE_CONFIRMATION}`} />
    </>
  );
};

export default Policies;
